// src/pages/TaskDetailPage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Divider,
  IconButton,
  Breadcrumbs,
  CircularProgress,
  Alert,
  Paper,
  Rating // Add Rating component
} from '@mui/material';
import {
  ArrowBack,
  Share,
  Bookmark,
  BookmarkBorder,
  Place,
  Schedule,
  CameraAlt,
  Security,
  VerifiedUser,
  CheckCircle,
  AttachMoney // Add AttachMoney icon
} from '@mui/icons-material';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyFormatter';
import DashboardLayout from '../components/DashboardLayout';

export default function TaskDetailPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserAssigned, setIsUserAssigned] = useState(false);
  const [userAssignmentStatus, setUserAssignmentStatus] = useState(null);

  // Get current user from localStorage on component mount
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setCurrentUser(JSON.parse(userData));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, []);

  // Fetch task details
  useEffect(() => {
    const fetchTaskDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            navigate('/login');
            return;
          }
          throw new Error('Failed to fetch task details');
        }

        const data = await response.json();
        console.log('Task API response:', data);
        
        // Handle different response structures
        const taskData = data.task || data;
        setTask(taskData);

        // Check if current user is assigned to this task
        if (currentUser && taskData.assigned_to === currentUser.id) {
          setIsUserAssigned(true);
        }

        // Calculate days remaining if deadline exists
        if (taskData.deadline) {
          const deadlineDate = new Date(taskData.deadline);
          const today = new Date();
          const diffTime = deadlineDate - today;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          setDaysRemaining(diffDays > 0 ? diffDays : 0);
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (taskId) {
      fetchTaskDetails();
    }
  }, [taskId, navigate, currentUser]);

  // Check user assignment status via API
  useEffect(() => {
    const checkUserAssignment = async () => {
      if (!currentUser?.id || !taskId) return;

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}/assignment-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setUserAssignmentStatus(data.status);
          setIsUserAssigned(data.is_assigned);
        }
      } catch (err) {
        console.error('Error checking assignment status:', err);
        // Fallback: check if task data shows assignment
        if (task?.assigned_to === currentUser.id) {
          setIsUserAssigned(true);
        }
      }
    };

    checkUserAssignment();
  }, [taskId, currentUser, task]);

  // Handle task acceptance
  const handleAcceptTask = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://propertyearn.net/api/api/tasks/${taskId}/accept`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to accept task');
      }

      // Update local state to reflect assignment
      setIsUserAssigned(true);
      alert('Task accepted successfully!');
      
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading task details...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      </DashboardLayout>
    );
  }

  if (!task) {
    return (
      <DashboardLayout>
        <Alert severity="warning" sx={{ m: 2 }}>
          Task not found
        </Alert>
      </DashboardLayout>
    );
  }

  // Format task data with fallbacks
  const taskTitle = task.title || 'Untitled Task';
  const taskDescription = task.description || 'No description available';
  const taskCategory = task.category || 'General';
  const taskReward = task.reward_amount || task.reward || 0;
  const taskAddress = task.address || 'Location not specified';
  const taskDeadline = task.deadline ? new Date(task.deadline).toLocaleDateString() : 'No deadline specified';
  const clientRating = task.clientRating || 0;
  const currencySymbol = getCurrencySymbol(task);
  const formattedReward = formatCurrency(taskReward, currencySymbol);

  // Extract poster information
  const posterName = task.poster_name || task.posterName || 'Verified User';
  const posterRating = task.poster_rating || task.posterRating || 0;

  return (
    <DashboardLayout>
      
      {/* Header Section */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }}>
            <Breadcrumbs aria-label="breadcrumb">
              <Typography color="text.primary">Marketplace</Typography>
              <Typography color="text.primary">{taskCategory}</Typography>
              <Typography color="text.primary">{taskTitle}</Typography>
            </Breadcrumbs>
          </Box>

          <IconButton>
            <Share />
          </IconButton>
          
          <IconButton onClick={() => setIsBookmarked(!isBookmarked)}>
            {isBookmarked ? <Bookmark color="primary" /> : <BookmarkBorder />}
          </IconButton>
        </Stack>
      </Box>

      {/* Main Content */}
      <Box sx={{ p: 3, maxWidth: '800px', margin: '0 auto' }}>
        {/* Task Title and Category */}
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          {taskTitle}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Chip icon={<CameraAlt />} label={taskCategory} color="primary" />
          <Chip
            icon={<AttachMoney />}
            label={formattedReward}
            color="success"
            variant="outlined"
          />
        </Stack>

        {/* Key Information Card */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'success.light' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
                {formattedReward}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reward upon completion
              </Typography>
              
              {/* Status indicator near reward */}
              {isUserAssigned && (
                <Chip 
                  label="Assigned to you" 
                  color="success" 
                  size="small"
                  sx={{ mt: 1 }}
                  icon={<CheckCircle />}
                />
              )}
            </Box>
            
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Schedule fontSize="small" />
                Due: {taskDeadline}
              </Typography>
              {daysRemaining > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>

        {/* Task Description */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            📝 Task Description
          </Typography>
          <Typography variant="body1" paragraph>
            {taskDescription}
          </Typography>
        </Box>

        {/* Enhanced Task Details Section (from TaskDetailCard) */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            📋 Task Details
          </Typography>
          
          <Stack spacing={2.5}>
            {/* Location Details */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Place color="action" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Location
                </Typography>
                <Typography variant="body1">
                  {taskAddress}
                </Typography>
              </Box>
            </Box>
            
            {/* Deadline Details */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Schedule color="action" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Deadline
                </Typography>
                <Typography variant="body1">
                  {taskDeadline}
                  {daysRemaining > 0 && (
                    <Typography variant="body2" color="primary.main" component="span" sx={{ ml: 1 }}>
                      ({daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining)
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Box>
            
            {/* Client Rating Details */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Security color="action" sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Client Rating & Trust
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Rating value={clientRating} precision={0.1} size="small" readOnly />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {clientRating}/5
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Based on previous task completions
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* Client Information */}
        <Paper elevation={1} sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            👤 Posted By
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <VerifiedUser color="primary" />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {posterName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ⭐ {posterRating}/5 • Task Poster
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Action Button */}
         <Box sx={{ mt: 4, mb: 2 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            sx={{
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600
            }}
            onClick={() => {
              if (isUserAssigned) {
                navigate(`/task/${taskId}/execute`);
              } else if (task.status === 'Published') {
                handleAcceptTask();
              } else {
                setError('This task is not available for acceptance');
              }
            }}
            disabled={(!task.status || task.status !== 'Published') && !isUserAssigned}
          >
            {isUserAssigned ? 'Continue Task' : 'Start This Task'} - {formattedReward}
          </Button>

          <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }} color="text.secondary">
            💰 Payment held in escrow until verification • ⏱️ 15-20 minutes to complete
          </Typography>
        </Box>
      </Box>
    </DashboardLayout>
  );
}